
export default function FeaturesSection() {
  const features = [
    {
      icon: 'ri-calendar-schedule-line',
      title: 'Automatic Planning & Reports',
      description: 'AI analyzes your audience and creates optimal posting schedules with detailed performance reports.',
      color: 'from-[#22577A] to-[#38a3a5]',
      bgColor: 'bg-[#e4f4ff]/20'
    },
    {
      icon: 'ri-share-line',
      title: 'Multi-Platform Scheduling',
      description: 'Schedule and publish content across Facebook, Instagram, Twitter, LinkedIn, and TikTok simultaneously.',
      color: 'from-[#38a3a5] to-[#66b3ff]',
      bgColor: 'bg-[#b3d9ff]/20'
    },
    {
      icon: 'ri-ai-generate',
      title: 'AI-Driven Content Creation',
      description: 'Generate engaging posts, captions, and hashtags tailored to each platform\'s best practices.',
      color: 'from-[#66b3ff] to-[#b3d9ff]',
      bgColor: 'bg-[#66b3ff]/20'
    },
    {
      icon: 'ri-settings-3-line',
      title: 'Smart Platform Adaptation',
      description: 'Automatically optimize language, hashtags, and tone for maximum engagement on each platform.',
      color: 'from-[#b3d9ff] to-[#e4f4ff]',
      bgColor: 'bg-[#e4f4ff]/30'
    },
    {
      icon: 'ri-calendar-event-line',
      title: 'AI Event Planner',
      description: 'Plan and coordinate blogs, media campaigns, and events with intelligent scheduling and reminders.',
      color: 'from-[#22577A] to-[#66b3ff]',
      bgColor: 'bg-[#38a3a5]/20'
    },
    {
      icon: 'ri-kanban-view',
      title: 'Kanban Team Collaboration',
      description: 'Seamless team workflow with drag-and-drop task management and real-time collaboration tools.',
      color: 'from-[#38a3a5] to-[#b3d9ff]',
      bgColor: 'bg-[#b3d9ff]/30'
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Analytics & Competitor Insights',
      description: 'Track campaign performance, monitor reach, and benchmark against competitors with real-time analytics.',
      color: 'from-[#22577A] to-[#66b3ff]',
      bgColor: 'bg-[#38a3a5]/20'
    },
    {
      icon: 'ri-layout-grid-line',
      title: 'Visual Feed Planner',
      description: 'Design aesthetic Instagram grids with moveable layouts and split photo capabilities.',
      color: 'from-[#e4f4ff] to-[#66b3ff]',
      bgColor: 'bg-[#e4f4ff]/20'
    },
    {
      icon: 'ri-folder-image-line',
      title: 'Media Library',
      description: 'Store and organize all your media files in one centralized, searchable library.',
      color: 'from-[#b3d9ff] to-[#38a3a5]',
      bgColor: 'bg-[#b3d9ff]/20'
    },
    {
      icon: 'ri-mail-send-line',
      title: 'Press Release & Email Blasts',
      description: 'Send press releases and email campaigns with hosting and tracking built-in.',
      color: 'from-[#66b3ff] to-[#22577A]',
      bgColor: 'bg-[#66b3ff]/20'
    },
    {
      icon: 'ri-file-list-3-line',
      title: 'Client Invoicing',
      description: 'Generate invoices, track expenses, and manage client payments seamlessly.',
      color: 'from-[#38a3a5] to-[#b3d9ff]',
      bgColor: 'bg-[#38a3a5]/20'
    },
    {
      icon: 'ri-link',
      title: 'Short Link Generation',
      description: 'Create branded short links with click tracking and analytics for every campaign.',
      color: 'from-[#22577A] to-[#e4f4ff]',
      bgColor: 'bg-[#22577A]/10'
    }
  ];

  return (
    <section id="features" className="py-20 bg-gradient-to-b from-white to-[#e4f4ff]/10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 bg-[#e4f4ff]/30 rounded-full text-[#22577A] font-medium mb-6">
            <i className="ri-star-line mr-2"></i>
            Powerful Features
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-[#22577A] mb-6">
            Everything You Need for
            <span className="block text-[#38a3a5]">Marketing Success</span>
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-700 max-w-3xl mx-auto">
            Our AI-powered platform combines intelligent automation with intuitive design to revolutionize your social media marketing strategy.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {features.map((feature, index) => (
            <div
              key={index}
              className={`${feature.bgColor} p-8 rounded-2xl border border-white/50 hover:shadow-xl transition-all duration-300 group cursor-pointer`}
            >
              <div className={`w-16 h-16 bg-gradient-to-br ${feature.color} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                <i className={`${feature.icon} text-white text-2xl`}></i>
              </div>
              
              <h3 className="text-xl font-bold text-[#22577A] mb-4">
                {feature.title}
              </h3>
              
              <p className="text-gray-700 leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>

        {/* Post Playground Feature */}
        <div className="mt-16 bg-gradient-to-r from-[#22577A] to-[#38a3a5] rounded-3xl p-8 lg:p-12 text-white">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <i className="ri-lightbulb-flash-line text-2xl"></i>
                </div>
                <h3 className="text-2xl font-bold">The Playground</h3>
              </div>

              <h4 className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold mb-6">
                AI-Powered Creative Content Hub
              </h4>

              <p className="text-lg text-white/90 mb-8 leading-relaxed">
                Get daily generated content ideas based on trending topics, holidays, historical events, and business milestones. Never run out of creative inspiration for your social media posts.
              </p>

              <div className="space-y-4 mb-8">
                <div className="flex items-center gap-3">
                  <i className="ri-check-line text-[#b3d9ff] text-xl"></i>
                  <span>247+ fresh content ideas generated daily</span>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-check-line text-[#b3d9ff] text-xl"></i>
                  <span>Real-time trend analysis & viral predictions</span>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-check-line text-[#b3d9ff] text-xl"></i>
                  <span>Holiday, history & business event tracking</span>
                </div>
              </div>

              <a
                href="/playground"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white text-[#22577A] rounded-xl font-semibold text-lg hover:shadow-2xl hover:scale-105 transition-all cursor-pointer"
              >
                <i className="ri-play-circle-line"></i>
                Explore Playground
              </a>
            </div>

            <div className="relative space-y-4">
              {/* Today's Featured Ideas */}
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <div className="flex items-center justify-between mb-4">
                  <h5 className="font-semibold flex items-center gap-2">
                    <i className="ri-fire-line text-orange-400"></i>
                    Today's Featured Ideas
                  </h5>
                  <span className="text-xs bg-white/20 px-3 py-1 rounded-full">Live</span>
                </div>

                <div className="space-y-3">
                  <div className="flex items-start gap-3 p-3 bg-white/10 rounded-lg hover:bg-white/20 transition-colors cursor-pointer">
                    <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
                      <img
                        src="/assets/cupcake-celebration.jpg"
                        alt="National Cupcake Day"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm mb-1">National Cupcake Day</div>
                      <div className="text-xs text-white/70 flex items-center gap-2">
                        <span className="flex items-center gap-1">
                          <i className="ri-arrow-up-line"></i>
                          +240%
                        </span>
                        <span>•</span>
                        <span>2.3M mentions</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 bg-white/10 rounded-lg hover:bg-white/20 transition-colors cursor-pointer">
                    <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
                      <img
                        src="https://images.unsplash.com/photo-1677442136019-21780ecad995?w=100&q=80"
                        alt="AI Marketing Revolution"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm mb-1">AI Marketing Revolution</div>
                      <div className="text-xs text-white/70 flex items-center gap-2">
                        <span className="flex items-center gap-1">
                          <i className="ri-arrow-up-line"></i>
                          +320%
                        </span>
                        <span>•</span>
                        <span>1.2M mentions</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 bg-white/10 rounded-lg hover:bg-white/20 transition-colors cursor-pointer">
                    <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
                      <img
                        src="https://images.unsplash.com/photo-1542601906990-b4d3fb778b09?w=100&q=80"
                        alt="Sustainability Trends 2025"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm mb-1">Sustainability Trends 2025</div>
                      <div className="text-xs text-white/70 flex items-center gap-2">
                        <span className="flex items-center gap-1">
                          <i className="ri-arrow-up-line"></i>
                          +290%
                        </span>
                        <span>•</span>
                        <span>890K mentions</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 bg-white/10 rounded-lg hover:bg-white/20 transition-colors cursor-pointer">
                    <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
                      <img
                        src="https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=100&q=80"
                        alt="Short-Form Video Content"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm mb-1">Short-Form Video Content</div>
                      <div className="text-xs text-white/70 flex items-center gap-2">
                        <span className="flex items-center gap-1">
                          <i className="ri-arrow-up-line"></i>
                          +450%
                        </span>
                        <span>•</span>
                        <span>3.8M mentions</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 bg-white/10 rounded-lg hover:bg-white/20 transition-colors cursor-pointer">
                    <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
                      <img
                        src="https://images.unsplash.com/photo-1516321497487-e288fb19713f?w=100&q=80"
                        alt="Behind-The-Scenes Content"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm mb-1">Behind-The-Scenes Content</div>
                      <div className="text-xs text-white/70 flex items-center gap-2">
                        <span className="flex items-center gap-1">
                          <i className="ri-arrow-up-line"></i>
                          +380%
                        </span>
                        <span>•</span>
                        <span>1.6M mentions</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 bg-white/10 rounded-lg hover:bg-white/20 transition-colors cursor-pointer">
                    <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
                      <img
                        src="https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=100&q=80"
                        alt="User-Generated Content"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm mb-1">User-Generated Content</div>
                      <div className="text-xs text-white/70 flex items-center gap-2">
                        <span className="flex items-center gap-1">
                          <i className="ri-arrow-up-line"></i>
                          +340%
                        </span>
                        <span>•</span>
                        <span>2.1M mentions</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 bg-white/10 rounded-lg hover:bg-white/20 transition-colors cursor-pointer">
                    <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
                      <img
                        src="https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=100&q=80"
                        alt="Mental Health Awareness"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm mb-1">Mental Health Awareness</div>
                      <div className="text-xs text-white/70 flex items-center gap-2">
                        <span className="flex items-center gap-1">
                          <i className="ri-arrow-up-line"></i>
                          +265%
                        </span>
                        <span>•</span>
                        <span>2.7M mentions</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Stats */}
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                <h5 className="font-semibold mb-4">Today's Content Stats</h5>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white/10 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold mb-1">342</div>
                    <div className="text-xs text-white/70">Ideas Generated</div>
                  </div>
                  <div className="bg-white/10 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold mb-1">58</div>
                    <div className="text-xs text-white/70">Trending Topics</div>
                  </div>
                  <div className="bg-white/10 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold mb-1">24</div>
                    <div className="text-xs text-white/70">Historical Events</div>
                  </div>
                  <div className="bg-white/10 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold mb-1">12</div>
                    <div className="text-xs text-white/70">Holiday Celebrations</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
